import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Check, Lock } from "lucide-react";
import { toast } from "sonner";
import { useSEO } from "../lib/useSEO";

const PLANS = [
  {
    id: "contractor_monthly",
    name: "Contractor",
    price: "$7.99",
    period: "/month",
    role: "contractor",
    features: ["Listed in directory", "Unlimited bidding", "Portfolio gallery", "Reviews & ratings", "Cancel anytime"],
  },
  {
    id: "homeowner_monthly",
    name: "Homeowner",
    price: "$10.99",
    period: "/month",
    role: "homeowner",
    highlight: true,
    features: ["Post unlimited projects", "Receive contractor bids", "Full directory access", "Priority listing", "Cancel anytime"],
  },
  {
    id: "one_time_pass",
    name: "Homeowner One-Time Fast Pass",
    price: "$6",
    period: "one-time · 1 week",
    role: "homeowner",
    features: ["1-week homeowner access", "Post unlimited projects", "Receive contractor bids", "Full directory access", "No recurring charges"],
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const gated = params.get("gated") === "1";
  const [loading, setLoading] = useState(null);
  useSEO({ title: "Pricing — Subscriptions & One-Time Pass", description: "Contractor subscriptions from $7.99/mo, homeowner subscriptions from $10.99/mo, or a $6 one-time fast pass. No long-term commitments." });

  const subscribe = async (pkg) => {
    if (!user) { navigate("/signup"); return; }
    setLoading(pkg);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkg,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setLoading(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-10 py-16">
      {gated && (
        <div className="max-w-2xl mx-auto mb-10 bg-[#FBF6EE] border-2 border-[#B85C47]/40 rounded-xl p-5 flex items-start gap-3">
          <Lock className="w-5 h-5 text-[#B85C47] shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-[#314A3E] mb-1" style={{ fontFamily: "Outfit" }}>Subscription required</div>
            <p className="text-sm text-stone-700">Kootenay Home Pros is members-only. Pick a plan below to unlock the directory, project board, and messaging.</p>
          </div>
        </div>
      )}
      <div className="text-center mb-14">
        <div className="overline text-[#B85C47] mb-3">Pricing</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-3" style={{ fontFamily: "Outfit" }}>Simple plans for the valley</h1>
        <p className="text-stone-600 max-w-xl mx-auto">No commitments. Cancel any time. Choose the plan that fits how you work.</p>
        <div className="inline-flex items-center gap-2 mt-5 px-4 py-2 bg-[#FBF6EE] border-2 border-[#B85C47]/40 rounded-full">
          <span className="w-2 h-2 rounded-full bg-[#B85C47] animate-pulse" />
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#B85C47]">Introductory Pricing — Limited Time</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {PLANS.map(p => (
          <div
            key={p.id}
            className={`bg-white border-2 rounded-xl p-8 flex flex-col ${
              p.highlight ? "border-[#B85C47] shadow-md relative" : "border-[#E2DCD0]"
            }`}
          >
            {p.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#B85C47] text-white text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1 rounded-full">Most Popular</div>}
            <h3 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>{p.name}</h3>
            <div className="mt-4 mb-2">
              <span className="text-4xl font-bold text-[#314A3E]" style={{ fontFamily: "Outfit" }}>{p.price}</span>
              <span className="text-stone-500 ml-1 text-sm">{p.period}</span>
            </div>
            <div className="mb-6">
              <span className="inline-block text-[10px] font-bold uppercase tracking-[0.15em] text-[#B85C47] bg-[#FBF6EE] px-2 py-0.5 rounded">
                Introductory rate
              </span>
            </div>
            <ul className="space-y-2.5 mb-8 flex-1">
              {p.features.map(f => (
                <li key={f} className="flex items-start gap-2 text-sm text-stone-700">
                  <Check className="w-4 h-4 text-[#314A3E] mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Button
              data-testid={`subscribe-${p.id}-btn`}
              onClick={() => subscribe(p.id)}
              disabled={loading === p.id}
              className={`w-full h-11 ${p.highlight ? "bg-[#B85C47] hover:bg-[#a04e3b]" : "bg-[#314A3E] hover:bg-[#243a30]"} text-white`}
            >
              {loading === p.id ? "Redirecting…" : "Choose plan"}
            </Button>
          </div>
        ))}
      </div>

      <p className="text-center text-xs text-stone-400 mt-10">Secure payments via Stripe · Cancel anytime in your dashboard.<br/>Introductory rates locked in for early members — prices subject to change for new sign-ups.</p>
    </div>
  );
}
